import axios from 'axios';
import axiosRetry from 'axios-retry';
import {
  Cliente,
  Produto,
  Pedido,
  NovoPedido,
  clienteSchema,
  produtoSchema,
  novoPedidoSchema,
  pagamentoSchema,
  Despesa,
  despesaSchema,
  Perfil,
} from '@erplight/shared';
import type {
  DescadastroInfo,
  PreferenciasEmail,
  ConsentimentoCookiesResponse,
  SalvarConsentimentoCookiesInput,
} from '@erplight/shared';
import type { DashboardResumo, Membro, Convite } from '@/types';

// Configure a URL base da API
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resiliência / Retry para conexões instáveis (Frente 4)
axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Nunca reexecuta POST: se a 1ª tentativa chegou ao servidor mas a
    // resposta se perdeu (comum em rede móvel), o retry cria o registro em
    // dobro — a API não tem chave de idempotência. Escritas que falham por
    // rede caem no outbox offline, que já cobre a retentativa.
    const method = (error.config?.method || '').toLowerCase();
    if (!['get', 'head', 'options', 'put', 'delete'].includes(method)) {
      return false;
    }
    if (axiosRetry.isNetworkOrIdempotentRequestError(error)) {
      return true;
    }
    if (error.response) {
      return error.response.status >= 500 || error.response.status === 429;
    }
    return false;
  },
});

// Interceptor para adicionar token
api.interceptors.request.use(
  async (config) => {
    if (import.meta.env.VITE_STAGE?.trim() === 'dev') {
      config.headers.Authorization = `Bearer fake-jwt-token`;
      return config;
    }

    try {
      const { configureAmplify } = await import('@/lib/amplify');
      configureAmplify();
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Falha ao obter sessão (não logado ou expirado), segue sem token
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Se receber 401 e não estivermos já na página de login, redireciona.
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
      try {
        const { default: authService } = await import('./auth');
        authService.logout();
      } catch {
        // Ignora falha de import do auth em logout
      }
      window.location.href = '/login';
    }

    // Tratamento de mensagens de erro em linguagem simples (Frente 4)
    if (!error.response) {
      error.friendlyMessage =
        'Sem conexão com a internet ou servidor indisponível. Verifique sua rede e tente novamente.';
    } else {
      const status = error.response.status;
      const data = error.response.data;

      if (status >= 500) {
        error.friendlyMessage =
          'Ocorreu um erro interno no servidor. Nossos engenheiros já foram notificados. Tente novamente em alguns instantes.';
      } else if (status === 403) {
        error.friendlyMessage = data?.message || 'Você não tem permissão para realizar esta ação.';
      } else if (status === 400 || status === 422) {
        // Se for erro do Zod (details array), não mostramos jargão, apenas repassamos uma mensagem compreensível.
        error.friendlyMessage =
          data?.message ||
          'Os dados enviados são inválidos. Verifique as informações e tente novamente.';
      } else if (status === 404) {
        error.friendlyMessage = data?.message || 'O registro solicitado não foi encontrado.';
      } else {
        error.friendlyMessage =
          data?.message || 'Ocorreu um erro inesperado. Tente novamente mais tarde.';
      }
    }

    throw error;
  }
);

// Instância axios crua (com os interceptors de auth/retry já configurados),
// exposta para o outbox offline reexecutar requisições enfileiradas.
export const apiClient = api;

/**
 * O fallback de SPA do CloudFront (`403/404 → 200 /index.html`) vale para a
 * distribuição inteira, inclusive `/api/*`: um token inválido volta como 200
 * com HTML. Sem esta checagem a página de descadastro leria `ativo` como
 * `undefined` e diria "Pronto, você não receberá mais" para um link quebrado.
 */
const exigirDescadastroInfo = (data: unknown): DescadastroInfo => {
  const info = data as Partial<DescadastroInfo> | null;
  if (!info || typeof info !== 'object' || typeof info.ativo !== 'boolean' || !info.tipo) {
    throw new Error('Resposta de descadastro inválida');
  }
  return info as DescadastroInfo;
};

const apiService = {
  // Despesas
  despesas: {
    listar: async (): Promise<Despesa[]> => {
      const { data } = await api.get('/despesas');
      return data.items || data;
    },
    criar: async (despesa: Omit<Despesa, 'id'>): Promise<Despesa> => {
      despesaSchema.parse(despesa);
      const { data } = await api.post('/despesas', despesa);
      return data;
    },
    atualizar: async (id: string, despesa: Partial<Despesa>): Promise<Despesa> => {
      despesaSchema.partial().parse(despesa);
      const { data } = await api.put(`/despesas/${id}`, despesa);
      return data;
    },
    excluir: async (id: string): Promise<void> => {
      await api.delete(`/despesas/${id}`);
    },
  },

  // Clientes
  clientes: {
    listar: async (): Promise<Cliente[]> => {
      const { data } = await api.get('/clientes');
      return data;
    },
    buscar: async (id: string): Promise<Cliente> => {
      const { data } = await api.get(`/clientes/${id}`);
      return data;
    },
    criar: async (cliente: Omit<Cliente, 'id'>): Promise<Cliente> => {
      // Prepara dados limpos para enviar
      const clienteLimpo = {
        nome: cliente.nome?.trim() || '',
        email: cliente.email?.trim() || '',
        telefone: cliente.telefone?.trim() || '',
        endereco: cliente.endereco?.trim() || '',
      };

      // Validação via schema unificado Zod
      clienteSchema.parse(clienteLimpo);

      const { data } = await api.post('/clientes', clienteLimpo);
      return data;
    },
    atualizar: async (id: string, cliente: Partial<Cliente>): Promise<Cliente> => {
      // Remove campos vazios para evitar problemas no backend
      const clienteLimpo: any = {};
      if (cliente.nome !== undefined) clienteLimpo.nome = cliente.nome?.trim() || '';
      if (cliente.email !== undefined) clienteLimpo.email = cliente.email?.trim() || '';
      if (cliente.telefone !== undefined) clienteLimpo.telefone = cliente.telefone?.trim() || '';
      if (cliente.endereco !== undefined) clienteLimpo.endereco = cliente.endereco?.trim() || '';
      if (cliente.ativo !== undefined) clienteLimpo.ativo = cliente.ativo;

      // Valida via schema Zod (partial check para aceitar atributos isolados)
      clienteSchema.partial().parse(clienteLimpo);

      const { data } = await api.put(`/clientes/${id}`, clienteLimpo);
      return data;
    },
  },

  // Produtos
  produtos: {
    // Listagem compatível com a versão antiga (retorna só o array)
    listar: async (lastKey?: string): Promise<Produto[]> => {
      const params = lastKey ? { lastKey } : undefined;
      const { data } = await api.get('/produtos', { params });
      return data;
    },

    // Listagem paginada (retorna array + lastKey via header)
    listarPaginado: async (lastKey?: string): Promise<{ data: Produto[]; lastKey?: string }> => {
      const params = lastKey ? { lastKey } : undefined;
      const response = await api.get('/produtos', { params });
      const xLastKey = response.headers['x-last-key'];

      // Auxiliar para confirmar o valor retornado pelo backend
      if (typeof window !== 'undefined') {
      }

      return {
        data: response.data,
        lastKey: xLastKey || undefined,
      };
    },

    buscar: async (id: string): Promise<Produto> => {
      const { data } = await api.get(`/produtos/${id}`);
      return data;
    },
    criar: async (produto: Omit<Produto, 'id'>): Promise<Produto> => {
      produtoSchema.parse(produto);
      const { data } = await api.post('/produtos', produto);
      return data;
    },
    atualizar: async (id: string, produto: Partial<Produto>): Promise<Produto> => {
      produtoSchema.partial().parse(produto);
      const { data } = await api.put(`/produtos/${id}`, produto);
      return data;
    },
    gerarUploadUrl: async (
      filename: string,
      contentType: string,
      fileSize: number
    ): Promise<{ uploadUrl: string; fileUrl: string }> => {
      const { data } = await api.post('/produtos/upload-url', { filename, contentType, fileSize });
      return data;
    },
  },

  // Pedidos
  // Resumo agregado da tela inicial (sessão 8b/8c)
  dashboard: {
    // `paraCobrar` normalizado na borda: o cache NetworkFirst do service worker
    // (pwa/vite.config.ts) pode devolver uma resposta antiga de /dashboard cujo
    // shape não tem mais esse campo, quebrando o Dashboard (issue #265).
    obterResumo: async (): Promise<DashboardResumo> => {
      const { data } = await api.get('/dashboard');
      return {
        ...data,
        paraCobrar: data.paraCobrar ?? { total: 0, quantidade: 0, itens: [] },
        ultimasVendas: data.ultimasVendas ?? [],
        totalPedidos: data.totalPedidos ?? 0,
      };
    },
  },

  relatorios: {
    obterRanking: async (dataInicio: string, dataFim: string) => {
      const params = new URLSearchParams({ dataInicio, dataFim });
      const { data } = await api.get(`/relatorios/ranking?${params.toString()}`);
      return data;
    },
  },

  pedidos: {
    listar: async (): Promise<Pedido[]> => {
      const { data } = await api.get('/pedidos');
      return data;
    },
    // Listagem paginada por data (desc) — cursor opaco via header X-Last-Key (sessão 8b)
    listarPaginado: async (lastKey?: string): Promise<{ data: Pedido[]; lastKey?: string }> => {
      const params: Record<string, string> = { limit: '20' };
      if (lastKey) params.lastKey = lastKey;
      const response = await api.get('/pedidos', { params });
      return {
        data: Array.isArray(response.data) ? response.data : [],
        lastKey: response.headers['x-last-key'] || undefined,
      };
    },
    buscar: async (id: string): Promise<Pedido> => {
      const { data } = await api.get(`/pedidos/${id}/detalhes`);
      return data;
    },
    criar: async (pedido: NovoPedido): Promise<Pedido> => {
      novoPedidoSchema.parse(pedido);
      const { data } = await api.post('/pedidos', pedido);
      return data;
    },
    registrarPagamento: async (id: string, valor: number, metodo: string): Promise<Pedido> => {
      pagamentoSchema.parse({ valor, metodo });
      const { data } = await api.post(`/pedidos/${id}/pagamentos`, { valor, metodo });
      return data;
    },
    estornarPagamento: async (pedidoId: string, pagamentoId: string): Promise<Pedido> => {
      const { data } = await api.delete(`/pedidos/${pedidoId}/pagamentos/${pagamentoId}`);
      return data.pedido;
    },
    // Orçamento → venda: o backend baixa o estoque na transição para Pendente
    converterEmVenda: async (id: string): Promise<Pedido> => {
      const { data } = await api.put(`/pedidos/${id}`, { status: 'Pendente' });
      return data;
    },
    cancelar: async (id: string): Promise<Pedido> => {
      const { data } = await api.put(`/pedidos/${id}`, { status: 'Cancelado' });
      return data;
    },
    // Marca "cobrança enviada agora" (Issue 10.2 — cobranças sugeridas)
    registrarCobranca: async (id: string): Promise<Pedido> => {
      const { data } = await api.post(`/pedidos/${id}/cobranca`);
      return data;
    },
  },

  // Estoque
  estoque: {
    registrarEntrada: async (
      produtoId: string,
      quantidade: number,
      observacao?: string,
      lancarDespesa?: boolean,
      valorDespesa?: number
    ) => {
      const { data } = await api.post(`/produtos/${produtoId}/estoque/entrada`, {
        quantidade,
        observacao,
        lancarDespesa,
        valorDespesa,
      });
      return data;
    },
    registrarRetirada: async (produtoId: string, quantidade: number, observacao?: string) => {
      const { data } = await api.post(`/produtos/${produtoId}/estoque/saida`, {
        quantidade,
        observacao,
      });
      return data;
    },
    listarMovimentacoes: async (produtoId: string) => {
      const { data } = await api.get(`/produtos/${produtoId}/estoque/movimentacoes`);
      return data;
    },
  },

  // Pagamentos
  getPagamentos: async (pedidoId?: string) => {
    const url = pedidoId ? `/pedidos/${pedidoId}/pagamentos` : '/pagamentos';
    const response = await api.get(url);
    return response.data;
  },
  registrarPagamento: async (pedidoId: string, data: any) => {
    const response = await api.post(`/pedidos/${pedidoId}/pagamentos`, data);
    return response.data;
  },

  // Equipe / Colaboradores (ADR 006/007 — Issue 5.15)
  membros: {
    listar: async (): Promise<{ membros: Membro[]; convites: Convite[] }> => {
      const { data } = await api.get('/membros');
      return data;
    },
    convidar: async (email: string): Promise<{ emailEnviado?: boolean }> => {
      const { data } = await api.post('/membros/convites', { email, role: 'colaborador' });
      return data;
    },
    alterarPapel: async (sub: string, role: 'owner' | 'colaborador') => {
      const { data } = await api.patch(`/membros/${sub}/role`, { role });
      return data;
    },
    revogarMembro: async (sub: string) => {
      const { data } = await api.delete(`/membros/${sub}`);
      return data;
    },
    revogarConvite: async (email: string) => {
      const { data } = await api.delete(`/membros/convites/${encodeURIComponent(email)}`);
      return data;
    },
    salvarPushSubscription: async (subscription: PushSubscription, horario: string) => {
      const { data } = await api.post('/membros/me/push', { subscription, horario });
      return data;
    },
    removerPushSubscription: async () => {
      const { data } = await api.delete('/membros/me/push');
      return data;
    },
    obterPreferencias: async (): Promise<PreferenciasEmail> => {
      const { data } = await api.get('/membros/me/preferencias');
      return data;
    },
    salvarPreferencias: async (preferencias: PreferenciasEmail): Promise<PreferenciasEmail> => {
      const { data } = await api.put('/membros/me/preferencias', preferencias);
      return data;
    },
    obterConsentimento: async (): Promise<ConsentimentoCookiesResponse> => {
      const { data } = await api.get('/membros/me/consentimento');
      return data;
    },
    salvarConsentimento: async (
      payload: SalvarConsentimentoCookiesInput
    ): Promise<ConsentimentoCookiesResponse> => {
      const { data } = await api.put('/membros/me/consentimento', payload);
      return data;
    },
  },

  // Perfil e Configurações
  getPerfil: async () => {
    const response = await api.get('/perfil');
    return response.data;
  },
  updatePerfil: async (data: Partial<Perfil>) => {
    const response = await api.put('/perfil', data);
    return response.data;
  },
  getPerfilUploadUrl: async (filename: string, contentType: string, fileSize: number) => {
    const response = await api.post('/perfil/upload-url', { filename, contentType, fileSize });
    return response.data;
  },
  deletarPerfil: async () => {
    const response = await api.delete('/perfil');
    return response.data;
  },

  // Rotas Públicas
  obterReciboPublico: async (token: string) => {
    const response = await api.get(`/public/recibo/${token}`);
    return response.data;
  },
  obterCatalogoPublico: async (tenantId: string) => {
    const response = await api.get(`/public/catalog/${tenantId}`);
    return response.data;
  },
  optOutCobranca: async (token: string) => {
    const response = await api.post(`/public/opt-out/${token}`);
    return response.data;
  },
  // Descadastro de e-mails (#363). O GET só lê; quem altera é o POST.
  obterDescadastro: async (token: string): Promise<DescadastroInfo> => {
    const response = await api.get(`/public/descadastro/${encodeURIComponent(token)}`);
    return exigirDescadastroInfo(response.data);
  },
  aplicarDescadastro: async (
    token: string,
    acao: 'descadastrar' | 'reativar'
  ): Promise<DescadastroInfo> => {
    const response = await api.post(`/public/descadastro/${encodeURIComponent(token)}`, { acao });
    return exigirDescadastroInfo(response.data);
  },
  restaurarConta: async (dados: { email: string; senha: string }) => {
    const response = await api.post('/public/restaurar-conta', dados);
    return response.data;
  },
  // Agenda & Parcelas
  obterAgenda: async () => {
    const response = await api.get('/agenda');
    return response.data;
  },
  registrarCobrancaParcela: async (orderId: string, numero: number) => {
    const response = await api.post(`/pedidos/${orderId}/parcelas/${numero}/cobranca`);
    return response.data;
  },
  // Billing
  billingCheckout: async (): Promise<{ url: string }> => {
    const response = await api.post('/billing/checkout');
    return response.data;
  },
  billingCancelSubscription: async (): Promise<{ message: string }> => {
    const response = await api.delete('/billing/subscription');
    return response.data;
  },

  // Recorrências
  listarRecorrencias: async () => {
    const response = await api.get('/recorrencias');
    return response.data;
  },
  criarRecorrencia: async (data: any) => {
    const response = await api.post('/recorrencias', data);
    return response.data;
  },
  atualizarRecorrencia: async (id: string, data: any) => {
    const response = await api.put(`/recorrencias/${id}`, data);
    return response.data;
  },
  removerRecorrencia: async (id: string) => {
    const response = await api.delete(`/recorrencias/${id}`);
    return response.data;
  },
  toggleRecorrencia: async (id: string, ativa: boolean) => {
    const response = await api.patch(`/recorrencias/${id}/status`, { ativa });
    return response.data;
  },
  processarRecorrencias: async () => {
    const response = await api.post('/recorrencias/processar');
    return response.data;
  },

  apiEndpoint: API_BASE_URL,
};

export default apiService;
